import { ifDefined } from 'lit/directives/if-defined.js'
import { html, nothing, TemplateResult } from 'lit'
import { CSEOption } from 'elements/option/option.js'


export interface SelectStrategy {
  readonly shouldToggleDropdown: boolean

  clearSelection(): void
  selectByOption(option: CSEOption): void
  selectByValue(value: string): void

  renderSelection(disabled: boolean): TemplateResult
}


export class SingleSelectStrategy implements SelectStrategy {
  private readonly allOptions: CSEOption[]
  private readonly valueUpdatedCallback: (value: string) => void

  private selectedOption: CSEOption | null = null

  constructor(allOptions: CSEOption[], valueUpdatedCallback: (value: string) => void) {
    this.allOptions = allOptions
    this.valueUpdatedCallback = valueUpdatedCallback
  }

  private valueUpdated() {
    this.valueUpdatedCallback(this.selectedOption?.value ?? '')
  }

  readonly shouldToggleDropdown = true

  clearSelection(): void {
    this.allOptions.forEach(option => { option.selected = false })
    this.selectedOption = null

    this.valueUpdated()
  }

  selectByOption(selectedOption: CSEOption): void {
    this.allOptions.forEach(option => { option.selected = option === selectedOption })
    this.selectedOption = selectedOption

    this.valueUpdated()
  }

  selectByValue(value: string): void {
    const optionFromValue = this.allOptions.find(option => option.value === value)
    if (optionFromValue !== undefined) {
      this.selectByOption(optionFromValue)
    } else {
      this.clearSelection()
    }
  }

  renderSelection(): TemplateResult {
    return html`
      <input class="selection" type="text" readonly autocomplete="off"
        value="${ifDefined(this.selectedOption?.labelText)}"
      />
    `
  }
}


export class MultiSelectStrategy implements SelectStrategy {
  private readonly allOptions: CSEOption[]
  private readonly valueUpdatedCallback: (value: string) => void
  private readonly maxOptionsVisible: number

  private selectedOptions: CSEOption[] = []

  constructor(allOptions: CSEOption[], valueUpdatedCallback: (value: string) => void, maxOptionsVisible: number) {
    this.allOptions = allOptions
    this.valueUpdatedCallback = valueUpdatedCallback
    this.maxOptionsVisible = maxOptionsVisible
  }

  private valueUpdated() {
    this.sortSelectedOptions()
    this.valueUpdatedCallback(this.selectedOptions.map(o => o.value).join(','))
  }

  readonly shouldToggleDropdown = false

  clearSelection(): void {
    this.allOptions.forEach(option => { option.selected = false })
    this.selectedOptions = []

    this.valueUpdated()
  }

  selectByOption(option: CSEOption): void {
    if (this.selectedOptions.includes(option)) {
      this.deselectOption(option)
    } else {
      this.selectOption(option)
    }
  }

  private deselectOption = (option: CSEOption) => {
    if (!this.selectedOptions.includes(option)) { return }

    const index = this.selectedOptions.indexOf(option)
    this.selectedOptions.splice(index, 1)
    option.selected = false

    this.valueUpdated()
  }

  private selectOption = (option: CSEOption) => {
    if (this.selectedOptions.includes(option)) { return }

    this.selectedOptions.push(option)
    option.selected = true

    this.valueUpdated()
  }

  private sortSelectedOptions() {
    const orderedOptions = this.allOptions
    this.selectedOptions.sort((a, b) => orderedOptions.indexOf(a) - orderedOptions.indexOf(b))
  }

  selectByValue(value: string): void {
    this.clearSelection()
    const values = value.split(',').map(value => value.trim())
    this.allOptions
      .filter(option => values.includes(option.value))
      .forEach(option => this.selectOption(option))

    this.valueUpdated()
  }

  renderSelection(disabled: boolean): TemplateResult {
    return html`
      <div class="selection-chips">
        ${this.selectedOptions.slice(0, this.maxOptionsVisible).map((option) => html`
          <cse-chip
            variant="dismissable"
            ?disabled="${disabled}"
            @cse-chip-dismissed="${() => this.deselectOption(option)}"
          >
            ${option.labelText}
          </cse-chip>`,
        )}
        ${this.selectedOptions.length > this.maxOptionsVisible ? html`
          <cse-text as="span" variant="body">...</cse-text>`
          : nothing
        }
      </div>
    `
  }
}
